'use client';

import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import type { SeedingShippingAddress } from '../types';
import { ShippingInfoSection } from './ShippingInfoSection';

/** 미설정 안내 보조 문구 — 서비스별 상이 (Figma). */
const EMPTY_DESCRIPTION = {
  musinsa: '배송지를 등록해 주세요.',
  default: '마이 > 회원정보 수정 페이지에서 배송지를 등록해 주세요.',
} as const;

interface SeedingShippingSectionProps {
  address: SeedingShippingAddress;
  onChangeAddress?: () => void;
  /**
   * 등록 상태에서 "배송지 변경" 버튼 노출 여부 (호출 측 status 정책).
   * 미설정 상태의 버튼 노출은 서비스(무신사 only)로 자동 결정되며 이 값과 무관.
   */
  changeable?: boolean;
}

/**
 * 시딩 상세 배송지 구좌 — 공유 `ShippingInfoSection` 에 서비스 분기(무신사/29CM)를 입힌다.
 *
 * 기본배송지 미설정(`registered === false`) 시:
 *  - 무신사: "배송지를 등록해 주세요." + 배송지 변경 버튼(opener 진입)
 *  - 29CM : "마이 > 회원정보 수정 페이지에서 배송지를 등록해 주세요." (변경 버튼 없음)
 */
export const SeedingShippingSection = ({
  address,
  onChangeAddress,
  changeable = false,
}: SeedingShippingSectionProps) => {
  const { service } = useDeviceInfo();
  const isMusinsa = service === SERVICE.MUSINSA;
  // registered 미지정(legacy mock) 은 등록된 것으로 간주.
  const registered = address.registered !== false;

  // 29CM 만 배송지명(별칭) 보유 → "배송지명 / 수령인" 병기(목록 표시와 통일). 별칭 없으면 수령인 단독.
  // 무신사는 수령인만(외부 order-group 지면도 별칭 미제공) → 항상 수령인. (NOTICKET 배송지 표시 통일)
  const recipientLabel =
    !isMusinsa && address.addressName
      ? `${address.addressName} / ${address.recipient}`
      : address.recipient;

  return (
    <ShippingInfoSection
      name={recipientLabel}
      address={`${address.address} ${address.addressDetail}`.trim()}
      phone={address.phone}
      registered={registered}
      emptyDescription={isMusinsa ? EMPTY_DESCRIPTION.musinsa : EMPTY_DESCRIPTION.default}
      // 등록 상태: 호출 측 정책(changeable). 미설정 상태: 무신사만(29CM 은 마이>회원정보 안내).
      showChangeButton={registered ? changeable : isMusinsa}
      onChangeAddress={onChangeAddress}
    />
  );
};
